import { useState } from "react";
import { useTheme } from "@mui/material";
import { useCollectionProductPageEngagement } from "../../../components/hooks/useCollectionProductPageEngagement";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSCircularProgress from "views/system-design/DSCircularProgress";

function CollectionProductPageEngagementWidget() {
  const theme = useTheme();
  const [days, setDays] = useState(30);
  const [inputValue, setInputValue] = useState("30");
  const {
    data: stats,
    isLoading,
    isError,
  } = useCollectionProductPageEngagement(days);

  const metrics = [
    {
      label: "Total clicks",
      value: stats?.totalClicks ?? 0,
      color: "#4285F4",
    },
    {
      label: "Unique artists",
      value: stats?.uniqueArtists ?? 0,
      color: "#34A853",
    },
  ];

  return (
    <DSCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
        }}
      >
        <DSTypography variant="h6">Product Page Clicks</DSTypography>
        <DSStack direction="row" alignItems="center" spacing={1}>
          <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
            Last
          </DSTypography>
          <DSTextField
            type="number"
            size="small"
            inputProps={{ min: 1, max: 365 }}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={() => {
              const parsed = parseInt(inputValue, 10);
              if (!isNaN(parsed) && parsed >= 1) {
                const clamped = Math.min(parsed, 365);
                setDays(clamped);
                setInputValue(String(clamped));
              } else {
                setInputValue(String(days));
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              }
            }}
            sx={{ width: 80 }}
          />
          <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
            days
          </DSTypography>
        </DSStack>
      </DSBox>
      <DSCardContent sx={{ flex: 1 }}>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : isError ? (
          <DSBox
            sx={{ textAlign: "center", p: "20px", color: "text.secondary" }}
          >
            Failed to load engagement data
          </DSBox>
        ) : (
          <DSStack
            direction="row"
            spacing={2}
            sx={{ justifyContent: "space-around", py: "12px" }}
          >
            {metrics.map((m) => (
              <DSBox key={m.label} sx={{ textAlign: "center" }}>
                <DSTypography
                  sx={{ fontSize: 36, fontWeight: 600, color: m.color }}
                >
                  {m.value.toLocaleString()}
                </DSTypography>
                <DSTypography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  {m.label}
                </DSTypography>
              </DSBox>
            ))}
          </DSStack>
        )}
      </DSCardContent>
      <DSBox
        sx={{ borderTop: `1px solid ${theme.palette.divider}`, px: 2, py: 1.5 }}
      >
        <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
          Artists clicking the collection "Product Page" preview button in the
          studio
        </DSTypography>
      </DSBox>
    </DSCard>
  );
}

export default CollectionProductPageEngagementWidget;
